import { Container } from '@smuchile/pkg-unimarc-components'
import { memo, useCallback, useRef } from 'react'
import {
  replaceStrings,
  trigger,
  useDeleteProductsIssues,
  useMobile,
  useUpdateQuantity
} from '@smuchile/pkg-unimarc-hooks'
import {
  ICartItem,
  ICartItemIssue,
  ICartProduct
} from '@smuchile/pkg-unimarc-hooks/shared/interfaces/INewCartUnimarc'
import { SwiperButton } from '../SwiperButton/SwiperButton'
import { BehaviorSwiper, Swiper } from './Swiper'
import { MemoShelf } from '../Body/ProductsTotalizers/Products/Shelves/Shelf'
import { ISimulationItem } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/ISimulation'
import { NotAvailableShelves } from 'components/Body/ProductsTotalizers/Products/NotAvailableShelves'

interface HandleOnSubmit {
  item?: ICartItem
  quantity: number
}

interface SwiperWraperProps {
  id: string
  item?: ICartItem
  isLoading: boolean
  isLoadingByEvent: boolean
  isProductsWithIssues?: boolean
  isProducts?: boolean
  orderItem?: ICartItemIssue
  products?: ICartProduct | Record<string, never>
  productDiscount?: ISimulationItem
  setLimitOnProductAlert?: (value: boolean) => void
}

export const SwiperWraper = memo(
  ({
    id,
    item,
    isLoading,
    isLoadingByEvent,
    isProductsWithIssues = false,
    isProducts,
    orderItem,
    products,
    productDiscount,
    setLimitOnProductAlert
  }: SwiperWraperProps) => {
    const refSwiper = useRef<BehaviorSwiper>(null)
    const { handleAddTempItem } = useUpdateQuantity()
    const { innerWidth } = useMobile()
    const isMobile = innerWidth < 1280

    const { handleDeleteProducts, isLoading: isLoadingOnDelete } =
      useDeleteProductsIssues()

    const handleDeleteProductsIssues = () => {
      handleDeleteProducts({
        productsIssues: [orderItem?.id]
      })

      if (isMobile && !refSwiper.current.isOpen) {
        refSwiper.current.open()
      } else if (isMobile && refSwiper.current.isOpen) {
        refSwiper.current.closed()
      }
    }

    const handleDeleted = () => {
      handleAddTempItem({ quantity: 0, item })
    }

    const upateItem = ({ quantity, item }) => {
      if (quantity != item?.price?.quantity) {
        let totalQuantity = quantity
        if (isProducts) {
          products.items.forEach(({ description, price }) => {
            if (
              description?.id == item.description.id &&
              replaceStrings(price?.sellingPrice) !=
                replaceStrings(item?.price?.sellingPrice)
            ) {
              totalQuantity += price.quantity
            }
          })
        }
        handleAddTempItem({
          quantity: totalQuantity,
          item,
          netContent: item.description.netContent
        })
      }
    }

    const handleOnSubmit = useCallback(
      ({ item, quantity }: HandleOnSubmit) => {
        trigger({
          eventType: 'sendTagInfo',
          data: {
            tagInfo: item?.tagInfo
          }
        })

        if (isMobile && !refSwiper.current.isOpen && quantity === 0) {
          refSwiper.current.open()
        } else if (isMobile && refSwiper.current.isOpen && quantity === 0) {
          refSwiper.current.closed()
        } else {
          upateItem({ quantity, item })
        }
        upateItem({ quantity, item })
      },
      [handleAddTempItem]
    )

    return (
      <Container>
        <Swiper
          buttonComponent={
            <SwiperButton
              onClick={
                isProductsWithIssues
                  ? handleDeleteProductsIssues
                  : handleDeleted
              }
            />
          }
          ref={refSwiper}
          swiper={isMobile}
        >
          {!isProductsWithIssues && (
            <MemoShelf
              bemId={id}
              handleOnSubmit={handleOnSubmit}
              isLoading={isLoading}
              isLoadingByEvent={isLoadingByEvent}
              isMobile={isMobile}
              item={item}
              productDiscount={productDiscount}
              setLimitOnProductAlert={setLimitOnProductAlert}
            />
          )}

          {isProductsWithIssues && orderItem && (
            <NotAvailableShelves
              bemId={id}
              handleDeleteProductsIssues={handleDeleteProductsIssues}
              isLoadingByEvent={isLoadingByEvent}
              isLoadingOnDelete={isLoadingOnDelete}
              isMobile={isMobile}
              orderItem={orderItem}
            />
          )}
        </Swiper>
      </Container>
    )
  }
)

SwiperWraper.displayName = 'SwiperWraper'
